/**
 * 객체 풀(object pool) — 고빈도·단명·동종 객체의 생성/소멸 churn(GC 압박)을 재사용으로 제거하는 순수 유틸.
 * 도메인·렌더 프레임워크 무관(Phaser/DOM 모름) — 재활성화/리셋 같은 부수효과는 전부 콜백에 위임한다.
 *
 * 사용 패턴: `acquire()`로 재사용(또는 신규) 객체를 얻고, 수명이 끝나면 `release(obj)`로 반납한다.
 * 반납된 객체는 idle 리스트에 보관되어 다음 `acquire()`에 재사용된다. 호출자는 acquire/release 콜백에서
 * 활성/비활성·상태 리셋(Phaser: setActive/visible·body.enable·tween kill 등)을 수행한다.
 *
 * `createdCount`/`reusedCount`로 재사용률을 진단할 수 있다(풀링 작동 입증 — 시각 검증 없이 수치로).
 */
export interface ObjectPoolOptions<T> {
  /** 신규 인스턴스 생성 — idle 풀이 비었을 때만 호출. (Phaser: `new Sprite(scene)` 등) */
  create: () => T;
  /** acquire 직후(신규/재사용 공통) — 활성화·재배치 hook. (Phaser: setActive(true)·body 재활성) */
  onAcquire?: (obj: T) => void;
  /** release 시(풀에 보관되는 경우만) — 비활성화·상태 리셋 hook. (Phaser: deactivate) */
  onRelease?: (obj: T) => void;
  /** idle 보관 상한 — 미지정(undefined)이면 무제한. 초과분 release는 onDiscard로 폐기된다. */
  maxIdle?: number;
  /** maxIdle 초과로 풀에 안 담는 release 시 — 영구 폐기 hook. (Phaser: destroy) onRelease 대신 호출된다. */
  onDiscard?: (obj: T) => void;
}

export class ObjectPool<T> {
  private readonly _free: T[] = [];
  /** idle 멤버십 집합 — release의 이중 반납 가드를 O(1)로(배열 includes 선형탐색 대신). _free와 동기 유지. */
  private readonly _idle = new Set<T>();
  private readonly _create: () => T;
  private readonly _onAcquire?: (obj: T) => void;
  private readonly _onRelease?: (obj: T) => void;
  private readonly _onDiscard?: (obj: T) => void;
  /** idle 보관 상한 — 무제한이면 Infinity. */
  private readonly _maxIdle: number;
  private _createdCount = 0;
  private _reusedCount = 0;

  constructor(options: ObjectPoolOptions<T>) {
    this._create = options.create;
    this._onAcquire = options.onAcquire;
    this._onRelease = options.onRelease;
    this._onDiscard = options.onDiscard;
    this._maxIdle =
      options.maxIdle === undefined ? Infinity : Math.max(0, Math.floor(options.maxIdle));
  }

  /** 현재 idle(재사용 대기) 객체 수. */
  get idleCount(): number {
    return this._free.length;
  }

  /** 지금까지 신규 생성한 누적 수 — 재사용이 잘되면 동시 피크 근처에서 더 안 늘어난다. */
  get createdCount(): number {
    return this._createdCount;
  }

  /** 지금까지 재사용(풀 hit)한 누적 수 — createdCount 대비 클수록 풀링 효과가 크다. */
  get reusedCount(): number {
    return this._reusedCount;
  }

  /** 재사용 객체(idle 있으면) 또는 신규 객체를 반환. onAcquire가 호출된다. */
  acquire(): T {
    const reused = this._free.pop();
    const obj = reused ?? this._create();
    if (reused === undefined) this._createdCount += 1;
    else {
      this._idle.delete(reused);
      this._reusedCount += 1;
    }
    this._onAcquire?.(obj);
    return obj;
  }

  /**
   * 객체를 풀에 반납 — onRelease 후 idle 보관(maxIdle 초과 시 onDiscard로 폐기).
   * 같은 객체를 두 번 반납하면(이중 release 가드, O(1)) 무시한다 — dead-sweep + fade onComplete 이중 호출 방어.
   */
  release(obj: T): void {
    if (this._idle.has(obj)) return;
    if (this._free.length >= this._maxIdle) {
      this._onDiscard?.(obj);
      return;
    }
    this._onRelease?.(obj);
    this._free.push(obj);
    this._idle.add(obj);
  }

  /** idle 객체를 모두 비운다 — onEach(예: destroy)를 각 객체에 호출 후 풀을 비운다. 카운터는 유지. */
  clear(onEach?: (obj: T) => void): void {
    if (onEach) for (const o of this._free) onEach(o);
    this._free.length = 0;
    this._idle.clear();
  }
}
